/**
 * Wishlist Feed - renders the shopper's wishlist on the wishlist page.
 *
 * Product-keyed, backed by window.WishlistStore. Logged-in shoppers receive a
 * server-rendered grid (the metafield is available in Liquid); logged-out
 * shoppers are rendered client-side by fetching product cards through the
 * Section Rendering API (`product-cards--wishlist`).
 *
 * Standalone asset (loaded directly on the wishlist page) - intentionally has no
 * imports so it can run without the bundler. Keep in sync with the store keys.
 */
const WISHLIST_FEED_STORAGE_KEY = 'electric-wishlist';
const WISHLIST_FEED_SECTION_ID = 'product-cards--wishlist';
const WISHLIST_FEED_BATCH_SIZE = 10;
const WISHLIST_FEED_CARD_SELECTOR = 'product-card[data-product-id]';

/**
 * Resolve the current wishlist product IDs from the best available source.
 * @returns {string[]}
 */
function readWishlistFeedIds() {
  if (window.WishlistStore && typeof window.WishlistStore.all === 'function') {
    return window.WishlistStore.all().map(String);
  }
  // Store not loaded yet: mirror its hydrate logic (localStorage ∪ SSR snapshot)
  // so a logged-in member's server-rendered cards are never wiped.
  const ids = new Set();
  try {
    const raw = localStorage.getItem(WISHLIST_FEED_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) parsed.map(String).filter(Boolean).forEach((id) => ids.add(id));
    }
  } catch (error) {
    void error;
  }
  if (Array.isArray(window.wishlistInitial)) {
    window.wishlistInitial.map(String).forEach((id) => ids.add(id));
  }
  return [...ids];
}

class WishlistFeed extends HTMLElement {
  /** @type {boolean} */
  #connected = false;
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {boolean} */
  #loading = false;

  connectedCallback() {
    if (this.#connected) return;
    this.#abortController = new AbortController();
    document.addEventListener('wishlist:changed', this.#handleChange.bind(this), {
      signal: this.#abortController.signal,
    });

    this.#importSharedWishlist();
    this.#reconcile();
    this.#connected = true;
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#connected = false;
  }

  get #container() {
    return this.querySelector('.wishlist-products');
  }

  get #loadingEl() {
    return this.querySelector('.wishlist-loading');
  }

  get #emptyEl() {
    return this.querySelector('.wishlist-empty');
  }

  /**
   * @returns {Set<string>}
   */
  #displayedIds() {
    const container = this.#container;
    const ids = new Set();
    if (!container) return ids;
    container.querySelectorAll(WISHLIST_FEED_CARD_SELECTOR).forEach((card) => {
      const id = card.getAttribute('data-product-id');
      if (id) ids.add(String(id));
    });
    return ids;
  }

  /**
   * Make the rendered grid match the wishlist: append what's missing, remove
   * what's no longer wished.
   * @private
   */
  async #reconcile() {
    const container = this.#container;
    if (!container) return;

    const target = readWishlistFeedIds();
    if (target.length === 0) {
      container.innerHTML = '';
      this.#showEmpty();
      return;
    }

    const displayed = this.#displayedIds();
    const targetSet = new Set(target);

    displayed.forEach((id) => {
      if (!targetSet.has(id)) this.#removeCard(id);
    });

    const missing = target.filter((id) => !displayed.has(id));
    if (missing.length > 0) {
      await this.#appendProducts(missing);
    } else {
      this.#updateVisibility();
    }
  }

  /**
   * @param {string[]} ids
   * @private
   */
  async #appendProducts(ids) {
    const container = this.#container;
    if (!container || ids.length === 0 || this.#loading) return;

    this.#loading = true;
    this.#ensureGrid(container);

    const batches = [];
    for (let i = 0; i < ids.length; i += WISHLIST_FEED_BATCH_SIZE) {
      batches.push(ids.slice(i, i + WISHLIST_FEED_BATCH_SIZE));
    }

    try {
      const results = await Promise.all(
        batches.map((batch, index) => this.#fetchBatch(batch, index).catch(() => '')),
      );
      const displayed = this.#displayedIds();
      for (const html of results) {
        if (!html) continue;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll(WISHLIST_FEED_CARD_SELECTOR).forEach((card) => {
          const id = card.getAttribute('data-product-id');
          if (!id || displayed.has(String(id))) return;
          container.insertAdjacentHTML('beforeend', card.outerHTML);
          displayed.add(String(id));
        });
      }
    } finally {
      this.#loading = false;
      this.#updateVisibility();
    }
  }

  /**
   * @param {string[]} ids
   * @param {number} index
   * @returns {Promise<string>}
   * @private
   */
  async #fetchBatch(ids, index) {
    const query = ids.map((id) => `id:${id}`).join(' OR ');
    const finalQuery = window.excludeDiscontinuedTag ? window.excludeDiscontinuedTag(query) : query;
    const root = (window.routes && window.routes.root_url) || '';
    const url = `${root}search?type=product&q=${encodeURIComponent(finalQuery)}&section_id=${WISHLIST_FEED_SECTION_ID}`;
    const response = await fetch(url, { priority: index === 0 ? 'high' : 'low' });
    if (!response.ok) throw new Error(`Wishlist fetch failed: ${response.status}`);
    return response.text();
  }

  /**
   * @param {CustomEvent} event
   * @private
   */
  #handleChange(event) {
    if (!this.#connected) return;
    const detail = event.detail || {};
    if (detail.action === 'remove' && detail.id) {
      this.#removeCard(String(detail.id));
      this.#updateVisibility();
    } else if (detail.action === 'add' && detail.id) {
      if (!this.#displayedIds().has(String(detail.id))) {
        this.#appendProducts([String(detail.id)]);
      }
    } else {
      this.#reconcile();
    }
  }

  /**
   * @param {string} id
   * @private
   */
  #removeCard(id) {
    const container = this.#container;
    if (!container) return;
    const card = container.querySelector(`product-card[data-product-id="${id}"]`);
    if (card) card.remove();
  }

  /**
   * @param {HTMLElement} container
   * @private
   */
  #ensureGrid(container) {
    if (!container.classList.contains('grid')) {
      container.classList.add('grid', 'grid-cols-2', 'md:grid-cols-3', 'lg:grid-cols-4', 'gap-4');
    }
  }

  /**
   * @private
   */
  #updateVisibility() {
    const container = this.#container;
    const hasCards = Boolean(container && container.querySelector(WISHLIST_FEED_CARD_SELECTOR));
    if (this.#loadingEl) this.#loadingEl.classList.add('hidden');
    if (hasCards) {
      this.#ensureGrid(container);
      container.classList.remove('hidden');
      if (this.#emptyEl) this.#emptyEl.classList.add('hidden');
    } else {
      this.#showEmpty();
    }
  }

  /**
   * @private
   */
  #showEmpty() {
    if (this.#loadingEl) this.#loadingEl.classList.add('hidden');
    if (this.#emptyEl) this.#emptyEl.classList.remove('hidden');
    if (this.#container) this.#container.classList.add('hidden');
  }

  /**
   * Import a shared wishlist (?products=id,id,...) into the store, then clean the URL.
   * @private
   */
  #importSharedWishlist() {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get('products') || params.get('wishlist');
      if (!raw) return;

      const ids = raw.split(',').map((value) => value.trim()).filter(Boolean);
      if (ids.length) {
        if (window.WishlistStore && typeof window.WishlistStore.add === 'function') {
          ids.forEach((id) => window.WishlistStore.add(id));
        } else {
          try {
            const current = JSON.parse(localStorage.getItem(WISHLIST_FEED_STORAGE_KEY) || '[]');
            const merged = new Set([
              ...(Array.isArray(current) ? current.map(String) : []),
              ...ids,
            ]);
            localStorage.setItem(WISHLIST_FEED_STORAGE_KEY, JSON.stringify([...merged]));
          } catch (error) {
            void error;
          }
        }
      }

      const url = new URL(window.location.href);
      url.searchParams.delete('products');
      url.searchParams.delete('wishlist');
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      void error;
    }
  }
}

if (!customElements.get('wishlist-feed')) {
  customElements.define('wishlist-feed', WishlistFeed);
}

/**
 * Copy text to the clipboard. Tries the async Clipboard API first (needs a
 * secure context + transient activation), then a legacy execCommand fallback so
 * it still works where the async API is blocked.
 * @param {string} text
 * @returns {Promise<boolean>} whether the copy succeeded
 */
async function copyToClipboard(text) {
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      void error; // fall through to the legacy path
    }
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch (error) {
    void error;
    return false;
  }
}

/**
 * Briefly swap the button label to a confirmation, preserving its inner markup
 * (icon) on restore.
 * @param {HTMLElement} button
 * @param {string} message
 */
function flashCopied(button, message) {
  if (button.dataset.copying === 'true') return;
  button.dataset.copying = 'true';
  const originalHTML = button.innerHTML;
  button.textContent = message;
  button.classList.add('opacity-75');
  setTimeout(() => {
    button.innerHTML = originalHTML;
    button.classList.remove('opacity-75');
    delete button.dataset.copying;
  }, 2000);
}

/**
 * Share wishlist: build a link carrying the current product IDs and copy it to
 * the clipboard. Opening that link re-adds the items to the visitor's wishlist
 * (see #importSharedWishlist). Prompts with the URL only if copying fails.
 */
document.addEventListener('click', async (event) => {
  const shareBtn = event.target.closest('[data-share-wishlist]');
  if (!shareBtn) return;
  event.preventDefault();

  const strings = window.wishlistStrings || {};
  const ids = readWishlistFeedIds();
  if (ids.length === 0) {
    alert(strings.shareEmpty || 'Your wishlist is empty. Add some products to share!');
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.set('products', ids.join(','));
  const shareUrl = url.toString();

  const copied = await copyToClipboard(shareUrl);
  if (copied) {
    flashCopied(shareBtn, strings.copied || 'Copied!');
  } else {
    window.prompt('Copy this link to share your wishlist:', shareUrl);
  }
});
